/**
 * Non-blocking telemetry bridge.
 *
 * Sentry Replay and PostHog are useful after startup, but together they made
 * up a large share of the JavaScript that had to download before React could
 * render. Keep the application-facing API tiny and load each vendor only when
 * it is configured. Shared promises guarantee initialization happens once
 * even when authentication and main startup race each other.
 */

let sentryModulePromise;
let postHogModulePromise;

const sentryConfigured = () => {
  const isProduction = window.location.hostname === 'app.arasynchealth.com'
    || window.location.hostname.includes('azurecontainerapps.io');
  return isProduction && Boolean(import.meta.env.VITE_SENTRY_DSN);
};

const postHogConfigured = () => Boolean(import.meta.env.VITE_POSTHOG_API_KEY);

const loadSentry = () => {
  if (!sentryConfigured()) return Promise.resolve(null);
  if (!sentryModulePromise) {
    sentryModulePromise = import('./sentry')
      .then((module) => {
        module.initSentry();
        return module;
      })
      .catch((error) => {
        console.error('Failed to load Sentry:', error);
        return null;
      });
  }
  return sentryModulePromise;
};

const loadPostHog = () => {
  if (!postHogConfigured()) return Promise.resolve(null);
  if (!postHogModulePromise) {
    postHogModulePromise = import('./posthog')
      .then((module) => {
        module.initPostHog();
        return module;
      })
      .catch((error) => {
        console.error('Failed to load PostHog:', error);
        return null;
      });
  }
  return postHogModulePromise;
};

export const initializeTelemetry = () => Promise.allSettled([
  loadSentry(),
  loadPostHog(),
]);

export const setTelemetryUser = (user) => Promise.allSettled([
  loadSentry().then((module) => module?.setSentryUser(user)),
  loadPostHog().then((module) => module?.identifyPostHogUser(user)),
]);

export const clearTelemetryUser = () => Promise.allSettled([
  loadSentry().then((module) => module?.clearSentryUser()),
  loadPostHog().then((module) => module?.resetPostHogUser()),
]);

export const captureTelemetryException = (error, options = {}) =>
  loadSentry().then((module) => {
    module?.captureSentryException(error, options);
  }).catch(() => undefined);
